import { Color3, type Mesh, MeshBuilder, Quaternion, StandardMaterial, Vector3 } from "@babylonjs/core";
import { HeroId, Slot, ck, hv, pk, zk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import { click, mallet, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { ClientHero } from "../types.ts";

const V = { webbed: 0, nestNet: 1, ult: 2 } as const;
const PK = { Cluster: pk(HeroId.PennyParka, 0), Snare: pk(HeroId.PennyParka, 1), Drone: pk(HeroId.PennyParka, 2) } as const;
const ZK = { Web: zk(HeroId.PennyParka, 0) } as const;
const CK = { Nest: ck(HeroId.PennyParka, 0), Mine: ck(HeroId.PennyParka, 1) } as const;

const webShot: Cue = trim(14, (o) => {
  click(o, 0.2, 3800, 0.004);
  tone(o, { f: 900, f1: 1400, glide: 0.07, gain: 0.1, d: 0.08 });
  noise(o, { color: "white", type: "bandpass", f: 3200, gain: 0.12, d: 0.08 });
  return 0.18;
});
const mechServo: Cue = trim(11, (o) => {
  thump(o, 0.25, 120, 80, 0.08);
  whoosh(o, 0.35, 700, 2400, 0.22);
  noise(o, { color: "crackle", type: "highpass", f: 2200, gain: 0.15, d: 0.16 });
  return 0.38;
});
const nestDrop: Cue = trim(4, (o) => {
  thump(o, 0.45, 90, 45, 0.18);
  [0, 4, 7].forEach((s, i) => {
    void i;
    mallet(o, 440 * 2 ** (s / 12), 0.12, 0.45);
  });
  return 0.7;
});

const theme = {
  bpm: 150,
  octave: 1,
  voice: mallet,
  notes: [
    [0, 0.25],
    [4, 0.25],
    [7, 0.25],
    [12, 0.5],
    [7, 0.25],
    [16, 0.75],
  ],
} satisfies ClientHero["audio"]["theme"];

type Vec3 = { x: number; y: number; z: number };
type Leg = { upper: Mesh; lower: Mesh; foot: Mesh; side: number; row: number; hip: Vec3; footHome: Vec3 };
type Gear = { cockpit: Mesh; pilotHead: Mesh; pilotHair: Mesh; carapace: Mesh; abdomen: Mesh; legs: Leg[]; nozzles: Mesh[]; hidden: Mesh[] };
const mat = (scene: Parameters<NonNullable<ClientHero["constructs"]>[number]>[0], hex: string, alpha = 1) => {
  const m = new StandardMaterial("penny-mat", scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.emissiveColor = Color3.FromHexString(hex).scale(0.25);
  m.alpha = alpha;
  return m;
};

function orientSegment(mesh: Mesh, a: Vec3, b: Vec3) {
  const mx = (a.x + b.x) * 0.5;
  const my = (a.y + b.y) * 0.5;
  const mz = (a.z + b.z) * 0.5;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const len = Math.max(0.001, Math.hypot(dx, dy, dz));
  mesh.position.set(mx, my, mz);
  mesh.scaling.y = len;
  const to = new Vector3(dx / len, dy / len, dz / len);
  const from = Vector3.Up();
  const axis = Vector3.Cross(from, to);
  const dot = Math.max(-1, Math.min(1, Vector3.Dot(from, to)));
  mesh.rotationQuaternion = axis.lengthSquared() < 1e-8
    ? Quaternion.RotationAxis(Vector3.Forward(), dot < 0 ? Math.PI : 0)
    : Quaternion.RotationAxis(axis.normalize(), Math.acos(dot));
}

const hero: ClientHero<Gear> = {
  id: HeroId.PennyParka,
  bulk: 1.38,
  shape: { head: 0.78, torso: 1.08, arms: 0.9, legs: 0.85 },
  gear(g) {
    const mech = g.toon("#26344a");
    const mechDark = g.toon("#11182e");
    const pink = g.toon("#e2556d");
    const yellow = g.toon("#ffd348");
    const glow = g.glow("#ff9bc4", 0.85);
    const skin = g.toon("#b97960");
    const hair = g.toon("#2b1f2e");
    const hidden = [g.rig.parts.head, g.rig.parts.armL, g.rig.parts.armR, g.rig.parts.legL, g.rig.parts.legR].filter((m): m is Mesh => !!m);
    hidden.forEach((m) => m.setEnabled(false));

    const carapace = g.add(MeshBuilder.CreateSphere("spider-carapace", { diameter: 1, segments: 16 }, g.scene), g.rig.torso, mech);
    carapace.scaling.set(1.05, 0.52, 0.82);
    carapace.position.set(0, -0.04, -0.02);
    const belly = g.add(MeshBuilder.CreateSphere("spider-belly-glow", { diameter: 0.32, segments: 10 }, g.scene), g.rig.torso, glow);
    belly.scaling.set(1.1, 0.42, 0.25);
    belly.position.set(0, -0.02, -0.58);
    const abdomen = g.add(MeshBuilder.CreateSphere("spider-abdomen", { diameter: 1, segments: 16 }, g.scene), g.rig.torso, mechDark);
    abdomen.scaling.set(0.78, 0.42, 0.82);
    abdomen.position.set(0, -0.08, 0.66);
    const stripe = g.add(MeshBuilder.CreateBox("abdomen-stripe", { width: 0.88, height: 0.08, depth: 0.08 }, g.scene), g.rig.torso, pink);
    stripe.position.set(0, 0.18, 0.1);
    const cockpit = g.add(MeshBuilder.CreateTorus("open-cockpit-rim", { diameter: 0.7, thickness: 0.045, tessellation: 28 }, g.scene), g.rig.torso, yellow);
    cockpit.rotation.x = Math.PI / 2;
    cockpit.position.set(0, 0.36, -0.3);
    const pilotHead = g.add(MeshBuilder.CreateSphere("penny-pilot-head", { diameter: 0.32, segments: 12 }, g.scene), g.rig.torso, skin);
    pilotHead.position.set(0, 0.57, -0.32);
    const pilotHair = g.add(MeshBuilder.CreateSphere("penny-pilot-hair", { diameter: 0.36, segments: 10, slice: 0.55 }, g.scene), g.rig.torso, hair);
    pilotHair.position.set(0, 0.66, -0.32);
    pilotHair.rotation.x = Math.PI;
    const visor = g.add(MeshBuilder.CreateBox("penny-goggles", { width: 0.22, height: 0.045, depth: 0.035 }, g.scene), g.rig.torso, yellow);
    visor.position.set(0, 0.58, -0.47);
    for (const side of [-1, 1]) {
      const shoulder = g.add(MeshBuilder.CreateSphere("penny-shoulder", { diameter: 0.18, segments: 8 }, g.scene), g.rig.torso, pink);
      shoulder.position.set(side * 0.18, 0.38, -0.28);
    }

    const legs: Leg[] = [];
    for (const side of [-1, 1]) {
      for (let row = 0; row < 4; row++) {
        const z = 0.5 - row * 0.34;
        const hip = { x: side * (0.46 + row * 0.035), y: -0.02 - row * 0.01, z };
        const footHome = { x: side * (1.1 + row * 0.22), y: -1.18, z: z - 0.12 - row * 0.08 };
        const upper = g.add(MeshBuilder.CreateCylinder("spider-upper-leg", { diameter: 0.075, height: 1, tessellation: 6 }, g.scene), g.rig.torso, row % 2 ? pink : mech);
        const lower = g.add(MeshBuilder.CreateCylinder("spider-lower-leg", { diameter: 0.065, height: 1, tessellation: 6 }, g.scene), g.rig.torso, mechDark);
        const foot = g.add(MeshBuilder.CreateBox("spider-foot", { width: 0.34, height: 0.08, depth: 0.16 }, g.scene), g.rig.torso, yellow);
        legs.push({ upper, lower, foot, side, row, hip, footHome });
      }
    }
    const nozzles: Mesh[] = [];
    for (const side of [-1, 1]) {
      const mount = g.add(MeshBuilder.CreateBox("web-nozzle-mount", { width: 0.22, height: 0.16, depth: 0.18 }, g.scene), g.rig.torso, pink);
      mount.position.set(side * 0.62, 0.0, -0.5);
      const barrel = g.add(MeshBuilder.CreateCylinder("web-nozzle", { diameter: 0.09, height: 0.38, tessellation: 10 }, g.scene), g.rig.torso, yellow);
      barrel.rotation.x = Math.PI / 2;
      barrel.position.set(side * 0.62, 0, -0.72);
      nozzles.push(mount, barrel);
    }
    return { cockpit, pilotHead, pilotHair, carapace, abdomen, legs, nozzles, hidden };
  },
  animate(gear, f) {
    gear.hidden.forEach((m) => m.setEnabled(false));
    const ultPulse = f.v[V.ult] > 0 ? 0.08 : 0;
    gear.carapace.scaling.set(1.05 + ultPulse, 0.52 + ultPulse * 0.4, 0.82 + ultPulse);
    gear.abdomen.scaling.set(0.78 + ultPulse, 0.42 + ultPulse * 0.4, 0.82 + ultPulse);
    gear.pilotHead.rotation.y = Math.sin(f.t * 2) * 0.08;
    gear.pilotHair.rotation.y = gear.pilotHead.rotation.y;
    const moving = f.speed > 0.25 || f.v[V.ult] > 0;
    const gait = moving ? f.t * (5.5 + Math.min(9, f.speed)) : f.t * 1.8;
    gear.legs.forEach(({ upper, lower, foot, side, row, hip, footHome }, i) => {
      const phase = gait + row * 1.25 + (side > 0 ? Math.PI : 0);
      const step = moving ? Math.sin(phase) : 0;
      const footLift = moving ? Math.max(0, step) * 0.16 : 0;
      const footPos = {
        x: footHome.x + side * step * (moving ? 0.1 : 0.015),
        y: footHome.y + footLift,
        z: footHome.z + Math.cos(phase) * (moving ? 0.12 : 0.025),
      };
      const knee = {
        x: (hip.x + footPos.x) * 0.5 + side * (0.28 + row * 0.06),
        y: 0.24 + Math.sin(phase) * (moving ? 0.08 : 0.025),
        z: (hip.z + footPos.z) * 0.5 + Math.cos(phase + row) * 0.04,
      };
      orientSegment(upper, hip, knee);
      orientSegment(lower, knee, footPos);
      foot.position.set(footPos.x, footPos.y, footPos.z);
      foot.rotation.y = side * 0.25 + Math.sin(phase) * 0.08;
      foot.rotation.z = Math.sin(phase) * 0.06;
      void i;
    });
    if (f.v[V.ult] > 0 || f.moveKind) return "sprint";
    if (f.action === 4) return "interact-right";
    return f.action === 1 || f.action === 2 ? "holding-right-shoot" : "idle";
  },
  hud(c, eid) {
    return [
      { label: "On web", value: hv(c, eid, V.webbed), color: "#ff9bc4" },
      { label: "Sweep", value: hv(c, eid, V.ult) / (12 * 60), color: "#f4ff4f" },
    ];
  },
  projectiles: Object.fromEntries([PK.Cluster, PK.Snare, PK.Drone].map((kind) => [kind, (scene) => {
    const mesh = MeshBuilder.CreateSphere("web-shot", { diameter: kind === PK.Snare ? 0.34 : 0.22, segments: 8 }, scene);
    mesh.material = mat(scene, kind === PK.Drone ? "#f4ff4f" : kind === PK.Snare ? "#ffffff" : "#ff9bc4", 0.9);
    return {
      gear: { mesh },
      update({ mesh }, f) {
        mesh.position.set(f.pos.x, f.pos.y, f.pos.z);
        mesh.scaling.setAll(1 + Math.sin(f.age * 30) * 0.12);
      },
      dispose: () => mesh.dispose(),
    };
  }])) as ClientHero["projectiles"],
  zones: {
    [ZK.Web]: (scene, frame) => {
      const disc = MeshBuilder.CreateTorus("web-disc", { diameter: frame.radius * 2, thickness: 0.035, tessellation: 32 }, scene);
      disc.material = mat(scene, "#ff9bc4", 0.35);
      return {
        gear: { disc },
        update({ disc }, f) {
          disc.position.set(f.pos.x, f.pos.y + 0.06, f.pos.z);
          disc.scaling.setAll(1 + Math.sin(f.age * 6) * 0.03);
        },
        dispose: () => disc.dispose(),
      };
    },
  },
  constructs: {
    [CK.Nest]: (scene) => {
      const root = MeshBuilder.CreateCylinder("spider-nest", { diameter: 1.6, height: 1.1, tessellation: 12 }, scene);
      root.material = mat(scene, "#e2556d", 0.9);
      const eye = MeshBuilder.CreateSphere("nest-eye", { diameter: 0.34, segments: 8 }, scene);
      eye.material = mat(scene, "#ffd348", 0.95);
      return {
        gear: { root, eye },
        update({ root, eye }, f) {
          root.position = new Vector3(f.pos.x, f.pos.y + 0.55, f.pos.z);
          root.rotation.y += 0.02;
          root.visibility = 0.5 + f.hpFrac * 0.45;
          eye.position = new Vector3(f.pos.x, f.pos.y + 0.9, f.pos.z - 0.62);
          eye.rotation.y = root.rotation.y;
          eye.visibility = root.visibility;
        },
        dispose: () => {
          root.dispose();
          eye.dispose();
        },
      };
    },
    [CK.Mine]: (scene) => {
      const mine = MeshBuilder.CreateCylinder("crawler-mine", { diameter: 0.7, height: 0.22, tessellation: 10 }, scene);
      mine.material = mat(scene, "#f4ff4f", 0.85);
      const legs = Array.from({ length: 4 }, (_, i) => {
        const l = MeshBuilder.CreateBox("mine-leg", { width: 0.32, height: 0.04, depth: 0.06 }, scene);
        l.material = mat(scene, "#11182e", 0.8);
        l.rotation.y = (i / 4) * Math.PI * 2;
        return l;
      });
      return {
        gear: { mine, legs },
        update({ mine, legs }: { mine: Mesh; legs: Mesh[] }, f) {
          mine.position = new Vector3(f.pos.x, f.pos.y + 0.11, f.pos.z);
          mine.rotation.y = f.yaw;
          mine.visibility = f.rel === "foe" ? 0.35 : 0.8;
          legs.forEach((l, i) => {
            const a = f.yaw + (i / 4) * Math.PI * 2;
            l.position = new Vector3(f.pos.x + Math.sin(a) * 0.38, f.pos.y + 0.1, f.pos.z + Math.cos(a) * 0.38);
            l.rotation.y = a;
            l.visibility = mine.visibility;
          });
        },
        dispose: () => {
          mine.dispose();
          legs.forEach((l) => l.dispose());
        },
      };
    },
  },
  icons: {
    [Slot.Primary]: "M4 12h16M6 8l12 8M6 16 18 8",
    [Slot.Secondary]: "M12 4v7M7 7l5 4 5-4M6 18c4-4 8-4 12 0",
    [Slot.Ability1]: "M12 5a4 4 0 0 1 4 4v6H8V9a4 4 0 0 1 4-4zM5 12h14M7 18h10",
    [Slot.Ability2]: "M8 13a4 4 0 1 0 8 0 4 4 0 0 0-8 0zM4 19l4-3M20 19l-4-3M6 8l3 2M18 8l-3 2",
    [Slot.Ability3]: "M5 17c5-10 9-10 14 0M7 19l-2-2 2-2M17 19l2-2-2-2",
    [Slot.Ultimate]: "M12 3v18M4 9h16M6 15h12M8 5l4-2 4 2",
    passive: "M4 18c4-8 12-8 16 0M8 14h8M12 6v12",
  },
  audio: {
    weight: "heavy",
    fire: (ctx) => ({ key: ctx?.alt ? "fire:penny:snare" : "fire:penny:web", cue: ctx?.alt ? mechServo : webShot }),
    ability: { [Slot.Ability1]: nestDrop, [Slot.Ability2]: mechServo, [Slot.Ability3]: mechServo, [Slot.Ultimate]: nestDrop },
    theme,
    themeTrim: { heroic: 1.5, hostile: -5.8, tease: 2.2 },
    loops: ({ v, alive }) => (alive && v[V.ult] > 0 ? [{ key: "penny-sweep", build: loops.jets, gain: 0.65 }] : []),
    flight: { [PK.Cluster]: loops.flight("orb"), [PK.Snare]: loops.flight("orb"), [PK.Drone]: loops.flight("orb") },
    zoneLoops: { [ZK.Web]: { build: loops.portal, gain: 0.22, range: 18 } },
    constructLoops: { [CK.Nest]: { build: loops.beacon, gain: 0.55, range: 28 } },
  },
};

export default hero;
